import { useMemo } from "react";
import { Outlet, useParams } from "react-router-dom";
import EventNavBar from "src/components/compounds/EventNavBar/EventNavBar";
import EventHeaderFinal from "src/components/compounds/EventHeader/EventHeaderFinal";
import EventHeaderUpcoming from "src/components/compounds/EventHeader/EventHeaderUpcoming";
import MainGrid from "src/components/atoms/MainGrid/MainGrid";
import {
  useGetTeamByIDQuery,
  useGetTeamEventWithSummaryByIDQuery,
  useGetTeamLiveGamesByIDQuery,
} from "src/state/modules/api";
import * as Event from "src/state/modules/event";
import { overlayLiveGames } from "src/utils/map-events";
import PageLoader1 from "src/components/atoms/PageLoader/PageLoader";
import ErrorBoundary from "src/components/atoms/ErrorBoundary/ErrorBoundary";
import { useGameState } from "src/components/util/useGameState";

const LIVE_POLL_MS = 30000;

export const PageLayout: React.FC = () => {
  const { id, eid } = useParams();

  const {
    data: event,
    error: eventErrror,
    isLoading: eventLoading,
  } = useGetTeamEventWithSummaryByIDQuery({ teamID: id ?? "", eventID: eid ?? "" });

  const { data: team, error: teamError, isLoading: teamLoading } = useGetTeamByIDQuery(id ?? "");

  // A game that's in progress has no game-summary yet, so /game-summaries/:eid returns 204 and
  // `event.gameData` is undefined — which would hide the Box Score / Plays / Game Stats / Videos
  // tabs and fall back to the "upcoming" header. Overlay the realtime /live feed (the same path the
  // schedule and Team Home use) so an in-progress game gets gameData → the tabs unhide and the
  // header renders its LIVE treatment. The summary stays authoritative once it exists.
  const { data: liveGames } = useGetTeamLiveGamesByIDQuery(id ?? "", {
    pollingInterval: LIVE_POLL_MS,
    skipPollingIfUnfocused: true,
  });
  const mergedEvent = useMemo(() => {
    if (!event || event.gameData) return event;
    return overlayLiveGames([event], liveGames ?? [])[0];
  }, [event, liveGames]);

  const isLive = mergedEvent?.gameData?.game_state === Event.GameState.LIVE;
  const isFinal = mergedEvent?.gameData !== undefined;

  // Built once here (for the header line score) and shared with the Box Score / Plays tabs via the
  // outlet context, so the engine isn't replayed separately on each tab.
  const gameState = useGameState(team, eid ?? "", { live: isLive, enabled: isFinal });

  if (eventLoading || !mergedEvent) return <PageLoader1 />;
  if (teamLoading || !team) return <PageLoader1 />;

  return (
    <MainGrid>
      {isFinal ? (
        <EventHeaderFinal team={team} event={mergedEvent} gameState={gameState} />
      ) : (
        <EventHeaderUpcoming team={team} event={mergedEvent} />
      )}
      <EventNavBar team={team} event={mergedEvent} isFinal={isFinal} />
      <ErrorBoundary>
        <Outlet context={{ team: team, event: mergedEvent, gameState }} />
      </ErrorBoundary>
    </MainGrid>
  );
};
